import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { Patient, Encounter, Vitals } from '../types';
import { ActivityIcon, CheckCircleIcon } from 'lucide-react';
export const NursingDashboard: React.FC = () => {
  const {
    patients,
    updateEncounter,
    addNotification
  } = useData();
  const [selectedEncounter, setSelectedEncounter] = useState<{
    patient: Patient;
    encounter: Encounter;
  } | null>(null);
  // Find all encounters that are currently with nursing
  const nursingQueue = patients.flatMap(p => p.encounters.filter(e => e.status === 'With Nursing').map(e => ({
    patient: p,
    encounter: e
  })));
  const initialVitals: Vitals = {
    temp: '',
    pulse: '',
    resp: '',
    bp: '',
    spO2: '',
    weight: '',
    height: '',
    bmi: '',
    painScore: '',
    avpu: ''
  };
  const [currentVitals, setCurrentVitals] = useState<Vitals>(initialVitals);
  const calculateBMI = (weight: string, height: string) => {
    const w = parseFloat(weight);
    const h = parseFloat(height); // assuming height in meters
    if (w > 0 && h > 0) {
      return (w / (h * h)).toFixed(1);
    }
    return '';
  };
  const handleVitalsChange = (field: keyof Vitals, value: string) => {
    const newVitals = {
      ...currentVitals,
      [field]: value
    };
    if (field === 'weight' || field === 'height') {
      newVitals.bmi = calculateBMI(newVitals.weight, newVitals.height);
    }
    setCurrentVitals(newVitals);
  };
  const handleSaveVitals = () => {
    if (!selectedEncounter) return;
    const {
      patient,
      encounter
    } = selectedEncounter;
    // Add new vitals to the array (up to 10)
    const updatedVitalsList = [...encounter.vitals, currentVitals].slice(0, 10);
    const updatedEncounter: Encounter = {
      ...encounter,
      vitals: updatedVitalsList,
      status: 'With Clinician'
    };
    updateEncounter(patient.id, updatedEncounter);
    addNotification({
      targetRole: 'Clinician',
      message: `Patient ready for consultation: ${patient.personalDetails.fullNames}`,
      patientId: patient.id
    });
    setSelectedEncounter(null);
    setCurrentVitals(initialVitals);
  };
  if (selectedEncounter) {
    const {
      patient,
      encounter
    } = selectedEncounter;
    // Gather all historical vitals from all encounters
    const allHistoricalVitals = patient.encounters.flatMap(e => e.vitals.map(v => ({
      ...v,
      date: e.date
    }))).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return <div className="bg-white shadow rounded-lg border border-gray-100 p-6" data-id="element-740">
        <div className="flex justify-between items-center mb-6 border-b pb-4" data-id="element-741">
          <div data-id="element-742">
            <h2 className="text-xl font-bold text-gray-800" data-id="element-743">
              Nursing Observations
            </h2>
            <p className="text-sm text-gray-500" data-id="element-744">
              Patient: {patient.personalDetails.fullNames} | File:{' '}
              {patient.personalDetails.fileNo} | Age:{' '}
              {patient.personalDetails.age}
            </p>
          </div>
          <button onClick={() => setSelectedEncounter(null)} className="text-gray-500 hover:text-gray-700" data-id="element-745">
            Back to Queue
          </button>
        </div>

        {/* Previous Vitals Table */}
        {allHistoricalVitals.length > 0 && <div className="mb-8" data-id="element-746">
            <h3 className="text-md font-medium text-gray-900 mb-3" data-id="element-747">
              Patient Vitals History
            </h3>
            <div className="overflow-x-auto max-h-60 overflow-y-auto border border-gray-200 rounded-md" data-id="element-748">
              <table className="min-w-full divide-y divide-gray-200 text-sm" data-id="element-749">
                <thead className="bg-gray-50 sticky top-0" data-id="element-750">
                  <tr data-id="element-751">
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-752">
                      Date
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-753">
                      Temp
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-754">
                      Pulse
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-755">
                      Resp
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-756">
                      BP
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-757">
                      SpO2
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-758">
                      Wt(kg)
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-759">
                      Ht(m)
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-760">
                      BMI
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-761">
                      Pain
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-762">
                      AVPU
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-763">
                  {allHistoricalVitals.map((v, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-764">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500" data-id="element-765">
                        {new Date(v.date).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2" data-id="element-766">{v.temp}</td>
                      <td className="px-3 py-2" data-id="element-767">{v.pulse}</td>
                      <td className="px-3 py-2" data-id="element-768">{v.resp}</td>
                      <td className="px-3 py-2" data-id="element-769">{v.bp}</td>
                      <td className="px-3 py-2" data-id="element-770">{v.spO2}</td>
                      <td className="px-3 py-2" data-id="element-771">{v.weight}</td>
                      <td className="px-3 py-2" data-id="element-772">{v.height}</td>
                      <td className="px-3 py-2" data-id="element-773">{v.bmi}</td>
                      <td className="px-3 py-2" data-id="element-774">{v.painScore}</td>
                      <td className="px-3 py-2" data-id="element-775">{v.avpu}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}

        {/* New Vitals Form */}
        <div className="bg-teal-50 p-4 rounded-md border border-teal-100" data-id="element-776">
          <h3 className="text-md font-medium text-teal-900 mb-4" data-id="element-777">
            New Observation (Reading {encounter.vitals.length + 1})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-778">
            <div data-id="element-779">
              <label className="block text-xs font-medium text-gray-700" data-id="element-780">
                Temp (°C)
              </label>
              <input type="text" value={currentVitals.temp} onChange={e => handleVitalsChange('temp', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-781" />
            </div>
            <div data-id="element-782">
              <label className="block text-xs font-medium text-gray-700" data-id="element-783">
                Pulse
              </label>
              <input type="text" value={currentVitals.pulse} onChange={e => handleVitalsChange('pulse', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-784" />
            </div>
            <div data-id="element-785">
              <label className="block text-xs font-medium text-gray-700" data-id="element-786">
                Resp
              </label>
              <input type="text" value={currentVitals.resp} onChange={e => handleVitalsChange('resp', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-787" />
            </div>
            <div data-id="element-788">
              <label className="block text-xs font-medium text-gray-700" data-id="element-789">
                BP
              </label>
              <input type="text" value={currentVitals.bp} onChange={e => handleVitalsChange('bp', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-teal-500 focus:border-teal-500" placeholder="120/80" data-id="element-790" />
            </div>
            <div data-id="element-791">
              <label className="block text-xs font-medium text-gray-700" data-id="element-792">
                SpO2 (%)
              </label>
              <input type="text" value={currentVitals.spO2} onChange={e => handleVitalsChange('spO2', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-793" />
            </div>
            <div data-id="element-794">
              <label className="block text-xs font-medium text-gray-700" data-id="element-795">
                Weight (Kg)
              </label>
              <input type="number" step="0.1" value={currentVitals.weight} onChange={e => handleVitalsChange('weight', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-796" />
            </div>
            <div data-id="element-797">
              <label className="block text-xs font-medium text-gray-700" data-id="element-798">
                Height (m)
              </label>
              <input type="number" step="0.01" value={currentVitals.height} onChange={e => handleVitalsChange('height', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-teal-500 focus:border-teal-500" placeholder="1.75" data-id="element-799" />
            </div>
            <div data-id="element-800">
              <label className="block text-xs font-medium text-gray-700" data-id="element-801">
                BMI
              </label>
              <input type="text" readOnly value={currentVitals.bmi} className="mt-1 block w-full bg-gray-100 border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm" data-id="element-802" />
            </div>
            <div data-id="element-803">
              <label className="block text-xs font-medium text-gray-700" data-id="element-804">
                Pain Score
              </label>
              <input type="text" value={currentVitals.painScore} onChange={e => handleVitalsChange('painScore', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-teal-500 focus:border-teal-500" placeholder="0-10" data-id="element-805" />
            </div>
            <div data-id="element-806">
              <label className="block text-xs font-medium text-gray-700" data-id="element-807">
                AVPU
              </label>
              <select value={currentVitals.avpu} onChange={e => handleVitalsChange('avpu', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-808">
                <option value="" data-id="element-809">Select...</option>
                <option value="Alert" data-id="element-810">Alert</option>
                <option value="Verbal" data-id="element-811">Verbal</option>
                <option value="Pain" data-id="element-812">Pain</option>
                <option value="Unresponsive" data-id="element-813">Unresponsive</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end" data-id="element-814">
            <button onClick={handleSaveVitals} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700" data-id="element-815">
              <CheckCircleIcon className="-ml-1 mr-2 h-5 w-5" data-id="element-816" />
              Save & Send to Clinician
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-817">
      <h2 className="text-xl font-bold text-gray-800" data-id="element-818">Nursing Queue</h2>

      <div className="bg-white shadow overflow-hidden sm:rounded-md border border-gray-100" data-id="element-819">
        <ul className="divide-y divide-gray-200" data-id="element-820">
          {nursingQueue.map(item => <li key={item.encounter.id} data-id="element-821">
              <div className="px-4 py-4 sm:px-6 hover:bg-gray-50 flex items-center justify-between" data-id="element-822">
                <div className="flex items-center" data-id="element-823">
                  <div className="flex-shrink-0" data-id="element-824">
                    <ActivityIcon className="h-8 w-8 text-teal-500" data-id="element-825" />
                  </div>
                  <div className="ml-4" data-id="element-826">
                    <p className="text-sm font-medium text-teal-600 truncate" data-id="element-827">
                      {item.patient.personalDetails.fullNames}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-828">
                      File No: {item.patient.personalDetails.fileNo} | Arrived:{' '}
                      {new Date(item.encounter.date).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                <div data-id="element-829">
                  <button onClick={() => setSelectedEncounter(item)} className="inline-flex items-center px-3 py-1 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none" data-id="element-830">
                    Take Vitals
                  </button>
                </div>
              </div>
            </li>)}
          {nursingQueue.length === 0 && <li className="px-4 py-8 text-center text-gray-500" data-id="element-831">
              No patients waiting for nursing observations.
            </li>}
        </ul>
      </div>
    </div>;
};